import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  SCOPE_LABEL,
  SCOPE_LABEL_KEY,
  STATUS_LABEL,
  STATUS_LABEL_KEY,
  cardCategoriesFromText,
  cardCategoryWords,
  describeCardCategories,
  formatCategoryList,
  isImportedPromotion,
  kindLabel,
  useActivatePromotion,
  useArchivePromotion,
  usePausePromotion,
  usePromotion,
  useUpdatePromotion,
} from '@/hooks/use-promotions';
import { useT } from '@/i18n/use-t';
import { cn } from '@/lib/utils';

function extractError(err: unknown): string | null {
  if (isAxiosError(err)) {
    const body = err.response?.data as { message?: string; code?: string } | undefined;
    if (body?.message) return body.code ? `${body.code}: ${body.message}` : body.message;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return null;
}

export function PromotionDetailPage() {
  const t = useT('admin_ui');
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = usePromotion(id);
  const update = useUpdatePromotion();
  const archive = useArchivePromotion();
  const pause = usePausePromotion();
  const activate = useActivatePromotion();

  const [displayName, setDisplayName] = useState('');
  const [priority, setPriority] = useState('100');
  const [configText, setConfigText] = useState('{}');
  const [cardInclude, setCardInclude] = useState('');
  const [cardExclude, setCardExclude] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  // Reset local form when the row changes (id swap OR version bump).
  useEffect(() => {
    if (!data) return;
    setDisplayName(data.displayName);
    setPriority(String(data.priority));
    setConfigText(JSON.stringify(data.config, null, 2));
    setCardInclude(formatCategoryList(data.cardCategories?.include));
    setCardExclude(formatCategoryList(data.cardCategories?.exclude));
    setOnlineOnly(data.onlineOnly === true);
    setBanner(null);
  }, [data?.id, data?.version]);

  const configParsed = useMemo(() => {
    try {
      return { ok: true as const, value: JSON.parse(configText) as Record<string, unknown> };
    } catch (err) {
      return { ok: false as const, error: (err as Error).message };
    }
  }, [configText]);

  if (isLoading)
    return <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>;
  if (!data)
    return (
      <p className="text-sm text-muted-foreground">
        {t('promotions.detail.notFound', 'Promotion not found.')}
      </p>
    );

  async function onSave() {
    if (!configParsed.ok || !data) {
      setBanner(
        configParsed.ok
          ? t('promotions.detail.notLoaded', 'Promotion not loaded')
          : `${t('promotions.detail.jsonParse', 'JSON parse:')} ${configParsed.error}`,
      );
      return;
    }
    setBanner(null);
    const nextCards = cardCategoriesFromText(cardInclude, cardExclude);
    const cardsChanged = JSON.stringify(nextCards) !== JSON.stringify(data.cardCategories ?? null);
    try {
      await update.mutateAsync({
        id: data.id,
        body: {
          expectedVersion: data.version,
          displayName: displayName.trim(),
          priority: Number(priority),
          config: configParsed.value,
          ...(cardsChanged ? { cardCategories: nextCards } : {}),
          // EAT-3: sent only when changed, like the card categories.
          ...(onlineOnly !== (data.onlineOnly === true) ? { onlineOnly } : {}),
        },
      });
    } catch (err) {
      setBanner(extractError(err) ?? t('promotions.detail.saveFailed', 'Save failed.'));
    }
  }

  async function runArchive() {
    setBanner(null);
    try {
      await archive.mutateAsync(data!.id);
    } catch (err) {
      setBanner(extractError(err) ?? t('promotions.detail.archiveFailed', 'Archive failed.'));
    }
  }

  async function runPauseOrActivate() {
    setBanner(null);
    try {
      if (data!.status === 'paused') {
        await activate.mutateAsync(data!.id);
      } else {
        await pause.mutateAsync(data!.id);
      }
    } catch (err) {
      setBanner(
        extractError(err) ?? t('promotions.detail.statusChangeFailed', 'Status change failed.'),
      );
    }
  }

  const isArchived = data.status === 'archived';
  // YUKI-176: imported rows are mastered in re:form Loyalty; BOS refuses edits.
  const isImported = isImportedPromotion(data);
  const readOnly = isArchived || isImported;

  return (
    <div className="space-y-5">
      <header>
        <Link to="/promotions" className="text-xs text-muted-foreground hover:underline">
          {t('promotions.nav.back', '← Promotions')}
        </Link>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold">{data.displayName}</h1>
          <Badge
            variant={
              data.status === 'active'
                ? 'success'
                : data.status === 'paused'
                  ? 'outline'
                  : 'destructive'
            }
          >
            {t(STATUS_LABEL_KEY[data.status], STATUS_LABEL[data.status])}
          </Badge>
          {isImported && (
            <Badge variant="outline">
              {data.source === 'connector'
                ? t('promotions.detail.importedFromStoreBadge', 'Imported from the store system')
                : t('promotions.detail.importedBadge', 'Imported from Loyalty')}
            </Badge>
          )}
        </div>
        <p className="font-mono text-xs text-muted-foreground">
          {data.code} · {kindLabel(data.kind, t)} ·{' '}
          {t(SCOPE_LABEL_KEY[data.scope], SCOPE_LABEL[data.scope])} · v{data.version}
        </p>
      </header>

      <section className="grid grid-cols-2 gap-4 rounded-md border border-border bg-card p-4">
        <div>
          <Label htmlFor="displayName">{t('promotions.field.displayName', 'Display name')}</Label>
          <Input
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={readOnly}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="priority">{t('promotions.field.priority', 'Priority')}</Label>
          <Input
            id="priority"
            type="number"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            disabled={readOnly}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="cardInclude">
            {t('promotions.field.cardInclude', 'Card categories — only for')}
          </Label>
          <Input
            id="cardInclude"
            value={cardInclude}
            onChange={(e) => setCardInclude(e.target.value)}
            disabled={readOnly}
            placeholder={t('promotions.detail.everyone', '(everyone)')}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="cardExclude">
            {t('promotions.field.cardExclude', 'Card categories — not for')}
          </Label>
          <Input
            id="cardExclude"
            value={cardExclude}
            onChange={(e) => setCardExclude(e.target.value)}
            disabled={readOnly}
            placeholder={t('promotions.detail.none', '(none)')}
            className="mt-1"
          />
        </div>

        <div className="col-span-2">
          <label htmlFor="onlineOnly" className="flex items-center gap-2 text-sm font-medium">
            <input
              id="onlineOnly"
              type="checkbox"
              checked={onlineOnly}
              onChange={(e) => setOnlineOnly(e.target.checked)}
              disabled={readOnly}
            />
            {t('promotions.field.onlineOnly', 'Online only')}
          </label>
          <p className="mt-1 text-xs text-muted-foreground">
            {t(
              'promotions.field.onlineOnlyHint',
              'Applied only while the till is online. A self-checkout that is offline skips this promotion and prices the basket without it. Tills not yet updated ignore this setting and apply the promotion offline too.',
            )}
          </p>
        </div>

        <div className="col-span-2">
          <Label htmlFor="config">{t('promotions.field.config', 'Config (JSON)')}</Label>
          <Textarea
            id="config"
            value={configText}
            onChange={(e) => setConfigText(e.target.value)}
            rows={14}
            spellCheck={false}
            disabled={readOnly}
            className={cn('mt-1 font-mono text-xs', readOnly && 'opacity-60')}
          />
          {!configParsed.ok && (
            <p className="mt-1 text-xs text-destructive">
              {t('promotions.form.jsonParseError', 'JSON parse error:')} {configParsed.error}
            </p>
          )}
        </div>

        {banner && (
          <p className="col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {banner}
          </p>
        )}

        {isImported && (
          <p className="col-span-2 text-xs text-muted-foreground">
            {t('promotions.detail.importedFrom', 'Imported from re:form Loyalty')}
            {data.externalId
              ? ` (${t('promotions.detail.importedCampaign', 'campaign')} ${data.externalId})`
              : ''}
            {data.importedAt
              ? `, ${t('promotions.detail.importedLastWritten', 'last written')} ${data.importedAt}`
              : ''}
            {'. '}
            {t(
              'promotions.detail.importedReadOnly',
              'It is read-only here: change the campaign in Loyalty and the next import carries it to the tills.',
            )}
          </p>
        )}

        <div className="col-span-2 flex flex-wrap gap-2">
          <Button onClick={onSave} disabled={readOnly || update.isPending || !configParsed.ok}>
            {update.isPending ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
          </Button>
          {!readOnly && (
            <>
              <Button
                onClick={runPauseOrActivate}
                disabled={pause.isPending || activate.isPending}
                variant="outline"
              >
                {data.status === 'paused'
                  ? t('promotions.detail.activate', 'Activate')
                  : t('promotions.detail.pause', 'Pause')}
              </Button>
              <Button onClick={runArchive} disabled={archive.isPending} variant="destructive">
                {archive.isPending
                  ? t('promotions.detail.archiving', 'Archiving…')
                  : t('promotions.detail.archive', 'Archive')}
              </Button>
            </>
          )}
        </div>

        {isArchived && !isImported && (
          <p className="col-span-2 text-xs text-muted-foreground">
            {t(
              'promotions.detail.archivedNote',
              'Archived promotions are permanent — create a new one with a different code to bring this rule back.',
            )}
          </p>
        )}
      </section>

      <section className="rounded-md border border-border bg-card p-4 text-xs">
        <header className="mb-2 text-sm font-medium">
          {t('promotions.detail.stackingFlags', 'Stacking flags')}
        </header>
        <dl className="space-y-1">
          <Row
            label={t('promotions.detail.exclusiveWithinBucket', 'Exclusive within bucket')}
            value={String(data.exclusiveWithinBucket)}
          />
          <Row
            label={t(
              'promotions.detail.stackableWithCustomerDiscount',
              'Stackable with customer discount',
            )}
            value={String(data.stackableWithCustomerDiscount)}
          />
          <Row
            label={t('promotions.detail.excludesPromotionCodes', 'Excludes promotion codes')}
            value={data.excludesPromotionCodes.join(', ') || t('promotions.detail.none', '(none)')}
          />
          <Row
            label={t('promotions.detail.couponCode', 'Coupon code')}
            value={data.couponCode ?? t('promotions.detail.none', '(none)')}
          />
          <Row
            label={t('promotions.field.appliesTo', 'Applies to')}
            value={describeCardCategories(data.cardCategories, cardCategoryWords(t))}
          />
          <Row
            label={t('promotions.detail.startsAt', 'Starts at')}
            value={data.startsAt ?? t('promotions.detail.immediately', '(immediately)')}
          />
          <Row
            label={t('promotions.detail.endsAt', 'Ends at')}
            value={data.endsAt ?? t('promotions.detail.noEnd', '(no end)')}
          />
        </dl>
      </section>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="w-56 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="flex-1 font-mono">{value}</dd>
    </div>
  );
}
